import { useState, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Product = {
  id: number
  sku: string
  name: string
  product_type: 'PRODUCT' | 'SERVICE' | 'CONSUMABLE'
  sales_price: string
  purchase_cost: string
  unit_of_measure: string
  is_active: boolean
  total_on_hand: string
}

// Minimal create payload — rest filled in on the record page.
type CreateInput = {
  sku: string
  name: string
  product_type: Product['product_type']
  sales_price: string
  purchase_cost: string
  unit_of_measure: string
}

const TYPE_COLOR: Record<Product['product_type'], string> = {
  PRODUCT: 'bg-blue-100 text-blue-800',
  SERVICE: 'bg-purple-100 text-purple-800',
  CONSUMABLE: 'bg-amber-100 text-amber-800',
}

const TYPE_LABEL: Record<Product['product_type'], string> = {
  PRODUCT: 'Storable',
  SERVICE: 'Service',
  CONSUMABLE: 'Consumable',
}

const TYPE_FILTERS: ('ALL' | Product['product_type'])[] = ['ALL', 'PRODUCT', 'SERVICE', 'CONSUMABLE']

export default function InventoryProducts() {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { confirm, dialog } = useDeleteConfirm()

  const productsQ = useQuery({
    queryKey: ['inventory', 'products'],
    queryFn: async () => (await api.get<Product[]>('/inventory/products/')).data,
  })

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState<CreateInput>(emptyDraft())
  const [filterType, setFilterType] = useState<'ALL' | Product['product_type']>('ALL')

  function openCreate() {
    setDraft(emptyDraft())
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Product>('/inventory/products/', {
      ...draft,
      description: '',
      default_tax_rate: '0',
      revenue_account: null,
      expense_account: null,
      stock_asset_account: null,
      is_active: true,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['inventory', 'products'] })
      toast.success('Product created')
      setCreating(false)
      navigate(`/admin/inventory/products/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const deleteMut = useMutation({
    mutationFn: async (id: number) => api.delete(`/inventory/products/${id}/`),
    onSuccess: () => {
      toast.success('Product deleted')
      qc.invalidateQueries({ queryKey: ['inventory', 'products'] })
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const products = productsQ.data || []
  const filtered = filterType === 'ALL' ? products : products.filter((p) => p.product_type === filterType)

  return (
    <AdminLayout>
      {dialog}

      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-foreground">Products</h2>
          <p className="text-sm text-muted-foreground">
            {products.length} total · {products.filter((p) => p.is_active).length} active
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="h-3 w-3 mr-1" /> New product
        </Button>
      </div>

      <div className="mb-4 flex items-center space-x-2 text-sm">
        <span className="text-muted-foreground">Type:</span>
        {TYPE_FILTERS.map((t) => (
          <button key={t} onClick={() => setFilterType(t)}
            className={`px-3 py-1 rounded ${filterType === t ? 'bg-foreground text-background' : 'bg-card text-foreground hover:bg-muted'}`}>
            {t === 'ALL' ? 'All' : TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      {productsQ.isLoading ? (
        <p className="text-muted-foreground">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="bg-card rounded-lg shadow p-8 text-center text-muted-foreground">No products.</div>
      ) : (
        <div className="bg-card rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted/40">
              <tr>
                <Th>SKU</Th>
                <Th>Name</Th>
                <Th>Type</Th>
                <Th>UoM</Th>
                <Th className="text-right">On hand</Th>
                <Th>Active</Th>
                <Th />
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  className="hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/inventory/products/${p.id}`)}
                >
                  <Td className="font-mono text-xs">{p.sku}</Td>
                  <Td className="font-medium">{p.name}</Td>
                  <Td>
                    <span className={`text-xs px-2 py-0.5 rounded font-semibold ${TYPE_COLOR[p.product_type]}`}>
                      {TYPE_LABEL[p.product_type]}
                    </span>
                  </Td>
                  <Td className="text-xs font-mono">{p.unit_of_measure}</Td>
                  <Td className="text-right font-mono text-xs">
                    {p.product_type === 'SERVICE'
                      ? <span className="text-muted-foreground">—</span>
                      : Number(p.total_on_hand).toFixed(2)}
                  </Td>
                  <Td className="text-xs">
                    {p.is_active
                      ? <span className="text-green-700">Active</span>
                      : <span className="text-muted-foreground">Inactive</span>}
                  </Td>
                  <Td className="text-right" onClick={(ev) => ev.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                          <MoreVertical className="h-4 w-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/inventory/products/${p.id}`)}>
                          Open
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => confirm(
                            `product ${p.sku} (${p.name})`,
                            () => deleteMut.mutate(p.id),
                          )}
                          className="text-destructive"
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create drawer — minimal fields. GL accounts + tax rate on the record page. */}
      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title="New product"
        description="Create the product with the essentials. Description, default tax rate, and GL accounts are filled in on the record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="pr-sku">SKU *</FieldLabel>
              <Input id="pr-sku" autoFocus required value={draft.sku}
                onChange={(ev) => setDraft({ ...draft, sku: ev.target.value })}
                className="font-mono" />
            </Field>
            <Field>
              <FieldLabel htmlFor="pr-type">Type</FieldLabel>
              <Select value={draft.product_type}
                onValueChange={(v) => setDraft({ ...draft, product_type: v as Product['product_type'] })}>
                <SelectTrigger id="pr-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PRODUCT">Storable</SelectItem>
                  <SelectItem value="SERVICE">Service</SelectItem>
                  <SelectItem value="CONSUMABLE">Consumable</SelectItem>
                </SelectContent>
              </Select>
              <FieldDescription>Storable / Consumable track stock. Service doesn't.</FieldDescription>
            </Field>
          </div>

          <Field>
            <FieldLabel htmlFor="pr-name">Name *</FieldLabel>
            <Input id="pr-name" required value={draft.name}
              onChange={(ev) => setDraft({ ...draft, name: ev.target.value })} />
          </Field>

          <Field>
            <FieldLabel htmlFor="pr-uom">Unit of measure</FieldLabel>
            <Input id="pr-uom" value={draft.unit_of_measure}
              onChange={(ev) => setDraft({ ...draft, unit_of_measure: ev.target.value })}
              placeholder="pcs, kg, hours, …" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <FieldLabel htmlFor="pr-sales">Sales price</FieldLabel>
              <Input id="pr-sales" type="number" step="0.01" value={draft.sales_price}
                onChange={(ev) => setDraft({ ...draft, sales_price: ev.target.value })}
                className="font-mono text-right" />
            </Field>
            <Field>
              <FieldLabel htmlFor="pr-cost">Purchase cost</FieldLabel>
              <Input id="pr-cost" type="number" step="0.01" value={draft.purchase_cost}
                onChange={(ev) => setDraft({ ...draft, purchase_cost: ev.target.value })}
                className="font-mono text-right" />
            </Field>
          </div>
        </FieldGroup>
      </RecordDrawer>
    </AdminLayout>
  )
}

function emptyDraft(): CreateInput {
  return {
    sku: '', name: '', product_type: 'PRODUCT',
    sales_price: '0', purchase_cost: '0', unit_of_measure: 'pcs',
  }
}

function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-sm text-foreground ${className}`} onClick={onClick}>{children}</td>
}
